import { assign, createMachine } from "xstate";
import { fetchFAQ, IFaqCategory } from "@nju/data-access-portal-help";
import { BasicError, isError } from "@nju/result";

export interface ICategory {
  name: string;
  id: string;
}

export interface IArticle {
  id: string;
  title: string;
  content: string;
  categoryId: string;
  categoryName: string;
}

interface IContext {
  categories: ICategory[];
  articles: IArticle[];
  /**
   * Articles filtered by searchValue
   */
  filteredArticles: IArticle[];
  activeCategoryId: undefined | string;
  activeArticleId: undefined | string;
  searchValue: string;
  error: BasicError | undefined;
}

type IEvent =
  | {
      type: "CLEAR_SEARCH";
    }
  | {
      type: "FETCH_ERROR";
      error: BasicError;
    }
  | {
      type: "FAQ_RECEIVED";
      data: IFaqCategory[];
    }
  | {
      type: "SELECT_CATEGORY";
      id: string | undefined;
    }
  | {
      type: "SET_SEARCH_VALUE";
      value: string;
    };

export const faqMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      categories: [],
      articles: [],
      filteredArticles: [],
      activeCategoryId: undefined,
      activeArticleId: undefined,
      searchValue: "",
      error: undefined,
    },
    initial: "init",
    on: {
      CLEAR_SEARCH: {
        actions: "clearSearch",
        target: "empty",
      },
    },
    states: {
      init: {
        invoke: {
          src: "fetchFAQ",
        },
        on: {
          FAQ_RECEIVED: {
            actions: "faqReceived",
            target: "empty",
          },
          FETCH_ERROR: {
            actions: "fetchError",
          },
        },
      },
      empty: {
        always: {
          cond: "isValueGreaterEqualThree",
          target: "active",
        },
        on: {
          SELECT_CATEGORY: {
            actions: "selectCategory",
          },
          SET_SEARCH_VALUE: {
            actions: "setSearchValue",
          },
        },
      },
      active: {
        entry: "resetSearchState",
        always: {
          cond: "isValueLessEqualTwo",
          actions: "selectFirstCategory",
          target: "empty",
        },
        on: {
          SET_SEARCH_VALUE: {
            actions: "setSearchValue",
          },
          SELECT_CATEGORY: {
            actions: "selectCategory",
          },
        },
      },
    },
  },
  {
    guards: {
      isValueGreaterEqualThree: ({ searchValue }) => {
        return searchValue.length >= 3;
      },
      isValueLessEqualTwo: ({ searchValue }) => {
        return searchValue.length <= 2;
      },
    },
    services: {
      fetchFAQ: () => {
        return async (callback) => {
          const result = await fetchFAQ();

          if (isError(result)) {
            return callback({ type: "FETCH_ERROR", error: result.err });
          }

          return callback({ type: "FAQ_RECEIVED", data: result.value });
        };
      },
    },
    actions: {
      clearSearch: assign<IContext, IEvent>(({ categories }, _) => {
        return {
          searchValue: "",
          activeCategoryId: categories[0].id,
        };
      }),
      faqReceived: assign<IContext, IEvent>((context, event) => {
        const { activeCategoryId } = context;

        if (event.type === "FAQ_RECEIVED") {
          const categories = event.data.map((element) => {
            return {
              id: element.categoryId,
              name: element.categoryName.pl,
            };
          });

          const articles = event.data.flatMap((category) => {
            return category.articles.map((article) => {
              return {
                id: article.articleId,
                title: article.title.pl,
                content: article.content.pl,
                categoryId: category.categoryId,
                categoryName: category.categoryName.pl,
              };
            });
          });

          const isActiveCategory = event.data.some((category) => {
            return category.categoryId === activeCategoryId;
          });

          return {
            activeCategoryId: isActiveCategory
              ? activeCategoryId
              : categories[0].id,
            categories,
            articles,
          };
        }

        return context;
      }),
      fetchError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_ERROR")
          return {
            error: event.error,
          };

        return context;
      }),
      setSearchValue: assign<IContext, IEvent>((context, event) => {
        const { articles, activeCategoryId } = context;

        if (event.type === "SET_SEARCH_VALUE") {
          const searchValue = event.value.replace("\\", "");

          return {
            activeCategoryId,
            searchValue: searchValue,
            filteredArticles: articles.filter((article) => {
              const re = new RegExp(searchValue, "i");
              return re.test(article.title) || re.test(article.content);
            }),
          };
        }

        return context;
      }),
      selectCategory: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SELECT_CATEGORY") {
          return {
            activeCategoryId: event.id,
          };
        }

        return context;
      }),
      resetSearchState: assign<IContext, IEvent>((_, __) => {
        return {
          activeCategoryId: undefined,
        };
      }),
      selectFirstCategory: assign<IContext, IEvent>(({ categories }, _) => {
        return {
          activeCategoryId: categories[0].id,
        };
      }),
    },
  },
);
